import React from "react";

// Server-side recording timeline (per-minute status + half-hourly snapshots),
// shared by CameraSection with the player's own timeline strip, so there is
// one strip that colours from the server and carries the snapshot markers.

export type TimelineSlot = { slot: string; status: "full" | "partial" | "none"; segments: number };
export type TimelineSnapshot = { time: string; url: string };

export interface RecordingTimeline {
  date: string; // YYYY-MM-DD
  slots: TimelineSlot[];
  snapshots: TimelineSnapshot[];
  selectedSnapshot: string | null; // HHMMSS
  openSnapshot: (time: string) => void;
}

export const RecordingTimelineContext = React.createContext<RecordingTimeline | null>(null);

export const STATUS_COLORS: Record<string, string> = {
  full: "#f59200",
  partial: "#FACC15",
  none: "#FCA5A5",
};

export const hhmmssToSeconds = (t: string) =>
  parseInt(t.slice(0, 2), 10) * 3600 + parseInt(t.slice(2, 4), 10) * 60 + parseInt(t.slice(4, 6) || "0", 10);

// Minute-by-minute status for the day. A past minute the board never reported
// is "none" (it was off); minutes still ahead today stay uncoloured.
export const minuteStatuses = (slots: TimelineSlot[], date: string): (string | null)[] => {
  const byMinute: (string | null)[] = new Array(1440).fill(null);
  slots.forEach((s) => {
    byMinute[Math.floor(hhmmssToSeconds(s.slot) / 60)] = s.status;
  });
  if (!slots.length) return byMinute;
  const now = new Date();
  const isToday = date === now.toLocaleDateString("en-CA");
  const lastKnown = Math.floor(hhmmssToSeconds(slots[slots.length - 1].slot) / 60);
  const cutoff = isToday ? now.getHours() * 60 + now.getMinutes() - 2 : 1440;
  for (let m = 0; m < Math.max(lastKnown, cutoff); m++) {
    if (byMinute[m] === null) byMinute[m] = "none";
  }
  return byMinute;
};
